/**
 * The kit's own editor extension (I-2799): the comment. A button and F6 in
 * every editor put a short text of the user's choice in, in a colour of their
 * choice — „Anna Demo (09.10.2026): “ before a remark in a mail's quote or a
 * note on a text. It has no panel; it writes at once.
 *
 * What it writes is a setting of the core key `editor` (the settings system,
 * like `table`): `comment_enabled`, `comment_text` with the placeholders
 * `{{user.name}}`, `{{user.email}}`, `{{date}}` and `{{time}}`, and
 * `comment_color`. The user comes from the placeholder values the app
 * provides for every editor; date and time are the moment it is written.
 */
import { computed, inject, type ComputedRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import { useNxSettings } from '../Settings/settingsSource';
import type { NxRichTextExtension, NxRichTextVariables } from './extensions';

/** The settings key of the editor's tools. */
export const NX_EDITOR_SETTINGS_KEY = 'editor';

/** The `editor` settings document as the kit reads it. */
export interface NxEditorSettings {
  comment_enabled?: boolean;
  comment_text?: string;
  comment_color?: string;
}

/** What applies while no layer sets a field (master's platform default). */
export const NX_EDITOR_COMMENT_DEFAULTS = {
  enabled: true,
  text: '{{user.name}} ({{date}}): ',
  color: '#3177b9',
} as const;

/** The colours a comment may have — the choice the settings offer. */
export const NX_EDITOR_COMMENT_COLORS = ['#3177b9', '#c0392b', '#2e7d32', '#d97706', '#7c3aed'];

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The comment's text with its placeholders filled in: the place's values
 * (`user.name` …), `date` and `time` of `now`. A placeholder nobody knows
 * stays as it is.
 */
export function commentText(
  template: string,
  variables: NxRichTextVariables,
  now: Date,
  locale: string
): string {
  const values: Record<string, string | undefined> = {
    ...variables,
    date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(now),
    time: new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(now),
  };
  return template.replace(PLACEHOLDER, (whole, key: string) => values[key] ?? whole);
}

/** The comment as HTML: its text in its colour, lines kept. */
export function commentHtml(text: string, color: string): string {
  const safe = NX_EDITOR_COMMENT_COLORS.includes(color) ? color : NX_EDITOR_COMMENT_DEFAULTS.color;
  return `<span style="color:${safe}">${escapeHtml(text).replace(/\r?\n/g, '<br>')}</span>`;
}

/** The kit's own extensions for an editor: the comment, while it is switched on. */
export function useRichTextKitExtensions(): ComputedRef<readonly NxRichTextExtension[]> {
  const settings = useNxSettings().get<NxEditorSettings>(NX_EDITOR_SETTINGS_KEY);
  const i18n = inject(i18nInjectionKey, null);
  const tr = (key: string, fallback: string) => {
    const full = `common.rich_text.${key}`;
    const text = i18n?.t(full);
    return text && text !== full ? text : fallback;
  };
  return computed(() => {
    const doc = settings.value ?? {};
    const template = doc.comment_text ?? NX_EDITOR_COMMENT_DEFAULTS.text;
    if (!(doc.comment_enabled ?? NX_EDITOR_COMMENT_DEFAULTS.enabled) || !template.trim()) return [];
    const color = doc.comment_color ?? NX_EDITOR_COMMENT_DEFAULTS.color;
    const comment: NxRichTextExtension = {
      id: 'editor.comment',
      label: tr('comment', 'Kommentar'),
      icon: 'comment',
      shortcut: 'F6',
      run: (context) =>
        context.insert(
          commentHtml(
            commentText(
              template,
              context.variables,
              new Date(),
              String(i18n?.locale.value ?? 'de')
            ),
            color
          )
        ),
    };
    return [comment];
  });
}
